import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader } from "../ui/Card";
import Button from "../ui/Button";
import { FileText, Save, Download, BarChart2, Table2, Shuffle } from "lucide-react";
import DateRangePicker from "./DateRangePicker";
import FiltersPopover from "./FilterPopover";
import { Sankey, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Legend } from "recharts";

const COLORS = ['#10B981', '#EF4444', '#3B82F6', '#F59E0B', '#8B5CF6'];

const TABS = [
  { label: 'Cash Flow', value: 'cashflow' },
  { label: 'Spending', value: 'spending' },
  { label: 'Income', value: 'income' },
];

interface ReportViewerProps {
  config: {
    chartType: 'line' | 'bar' | 'pie' | 'table' | 'sankey';
    metrics: string[];
  };
  data: any[];
}

const presetToRange = (preset: string): [Date, Date] => {
  const today = new Date();
  switch (preset) {
    case 'last7':
      return [new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6), today];
    case 'last30':
      return [new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29), today];
    case 'thisMonth':
      return [new Date(today.getFullYear(), today.getMonth(), 1), today];
    case 'lastMonth':
      return [
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        new Date(today.getFullYear(), today.getMonth(), 0),
      ];
    case 'thisYear':
      return [new Date(today.getFullYear(), 0, 1), today];
    case 'lastYear':
      return [new Date(today.getFullYear() - 1, 0, 1), new Date(today.getFullYear() - 1, 11, 31)];
    default:
      return [today, today];
  }
};

function formatDate(dateStr: string) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const ReportViewer: React.FC<ReportViewerProps> = ({ config, data }) => {
  const [activeTab, setActiveTab] = useState("cashflow");
  const [dateRange, setDateRange] = useState<any>({ type: "preset", preset: "last7" });
  const [filters, setFilters] = useState<any>({});
  const [chartType, setChartType] = useState<"sankey" | "bar" | "table">("sankey");
  const [groupBy, setGroupBy] = useState<"category_group" | "category" | "group">("category_group");

  // Date Range logic
  const [from, to] = useMemo(() => {
    if (dateRange.type === "preset") return presetToRange(dateRange.preset);
    if (dateRange.type === "range")
      return [
        dateRange.startDate ? new Date(dateRange.startDate) : new Date("1970-01-01"),
        dateRange.endDate ? new Date(dateRange.endDate) : new Date(),
      ];
    return [new Date("1970-01-01"), new Date()];
  }, [dateRange]);

  // Filtered Data (category filter is example only, you can expand as needed)
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      const d = new Date(row.date);
      return (
        d >= new Date(from.getFullYear(), from.getMonth(), from.getDate()) &&
        d <= new Date(to.getFullYear(), to.getMonth(), to.getDate())
      );
    });
  }, [data, from, to]);

  // Metrics
  const totalIncome = filteredData.reduce((sum: number, row: any) => sum + (row.income || 0), 0);
  const totalExpenses = filteredData.reduce((sum: number, row: any) => sum + (row.expenses || 0), 0);
  const totalNetIncome = totalIncome - totalExpenses;
  const savingsRate = totalIncome > 0 ? ((totalNetIncome / totalIncome) * 100) : 0;

  const metrics = [
    { label: "Total Income", value: totalIncome, color: "text-emerald-600" },
    { label: "Total Expenses", value: totalExpenses, color: "text-red-500" },
    { label: "Total Net Income", value: totalNetIncome, color: "text-gray-900" },
    { label: "Savings Rate", value: `${savingsRate.toFixed(1)}%`, color: "text-gray-900" },
  ];

  // Example Sankey data transformation
  // You MUST adapt this to your own data shape for real use!
  const sankeyData = {
    nodes: [
      { name: "Income" },
      { name: "Savings" },
      { name: "Expenses" },
      { name: "Financial" },
      { name: "Cash & ATM" },
    ],
    links: [
      { source: 0, target: 1, value: totalNetIncome > 0 ? totalNetIncome : 0 },
      { source: 0, target: 2, value: totalExpenses },
      { source: 2, target: 3, value: 99955.39 }, // just demo
      { source: 3, target: 4, value: 41920 }, // just demo
    ],
  };

  // Chart render logic
  function renderChart() {
    if (activeTab === "cashflow") {
      // Chart controls: dropdown + chart toggles + download
      return (
        <div>
          {/* Chart controls row */}
          <div className="flex items-center justify-between mb-4 mt-6 w-full">
            {/* Section Label and Date Range */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
                CASH FLOW
              </div>
              <div className="text-base font-semibold text-gray-900">
                {from && to ? `${formatDate(from.toISOString())} – ${formatDate(to.toISOString())}` : "All time"}
              </div>
            </div>
            {/* Controls */}
            <div className="flex items-center gap-2">
              {/* Dropdown */}
              <select
                className="border rounded-md px-3 py-1 text-sm bg-white font-medium focus:outline-none"
                value={groupBy}
                onChange={e => setGroupBy(e.target.value as any)}
              >
                <option value="category_group">By category & group</option>
                <option value="category">By category</option>
                <option value="group">By group</option>
              </select>
              <span className="mx-2 w-px h-6 bg-gray-200" />
              {/* Chart type toggles */}
              <button
                className={`p-2 rounded-lg ${chartType === "sankey" ? "bg-orange-50 border border-orange-200 text-orange-600" : "hover:bg-gray-50"}`}
                onClick={() => setChartType("sankey")}
                title="Sankey"
              >
                <Shuffle size={18} />
              </button>
              <button
                className={`p-2 rounded-lg ${chartType === "bar" ? "bg-orange-50 border border-orange-200 text-orange-600" : "hover:bg-gray-50"}`}
                onClick={() => setChartType("bar")}
                title="Bar"
              >
                <BarChart2 size={18} />
              </button>
              <button
                className={`p-2 rounded-lg ${chartType === "table" ? "bg-orange-50 border border-orange-200 text-orange-600" : "hover:bg-gray-50"}`}
                onClick={() => setChartType("table")}
                title="Table"
              >
                <Table2 size={18} />
              </button>
              <button
                className="p-2 rounded-lg hover:bg-gray-50"
                title="Download"
                onClick={() => alert("Download not implemented")}
              >
                <Download size={18} />
              </button>
            </div>
          </div>
          {/* Chart rendering */}
          <div className="min-h-[400px] bg-gray-50 border rounded-lg flex items-center justify-center">
            {chartType === "sankey" ? (
              <ResponsiveContainer width="100%" height={400}>
                <Sankey data={sankeyData} nodePadding={24} linkCurvature={0.5}>
                  <RechartsTooltip />
                </Sankey>
              </ResponsiveContainer>
            ) : chartType === "bar" ? (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={filteredData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="expenses" fill="#EF4444" name="Expenses" />
                </BarChart>
              </ResponsiveContainer>
            ) : chartType === "table" ? (
              <div className="overflow-x-auto w-full">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                    <tr>
                      <th className="px-4 py-2">Date</th>
                      <th className="px-4 py-2">Income</th>
                      <th className="px-4 py-2">Expenses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((row, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="px-4 py-2">{row.date}</td>
                        <td className="px-4 py-2">${row.income?.toLocaleString()}</td>
                        <td className="px-4 py-2">${row.expenses?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      );
    }
    // Add Spending/Income tab views as needed
    return (
      <div className="min-h-[400px] flex items-center justify-center text-gray-400 italic">
        {activeTab === "spending" ? "Spending charts coming soon..." : "Income charts coming soon..."}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-col gap-4 pb-0">
        {/* Top Section: Tabs & Actions */}
        <div className="flex items-center justify-between w-full">
          {/* Left: Title & Tabs */}
          <div className="flex items-end gap-8">
            <span className="font-bold text-lg text-gray-900">Reports</span>
            <div className="flex gap-6">
              {TABS.map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  className={`pb-2 border-b-2 transition-all font-medium ${
                    activeTab === tab.value
                      ? "border-orange-500 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-800"
                  }`}
                  onClick={() => setActiveTab(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          {/* Right: Actions (only for Cash Flow) */}
          <div className="flex items-center gap-2">
            {activeTab === "cashflow" && (
              <>
                <DateRangePicker onApply={setDateRange} />
                <FiltersPopover onApply={setFilters} />
                <span className="mx-2 text-gray-300">|</span>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-2 bg-white"
              type="button"
            >
              <FileText size={16} /> Reports
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="bg-orange-100 hover:bg-orange-200 text-orange-700 font-medium flex items-center gap-2 px-4 py-2 rounded-lg ml-2"
              type="button"
            >
              <Save size={16} /> Save
            </Button>
          </div>
        </div>
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 w-full mt-4">
          {metrics.map((metric) => (
            <div
              key={metric.label}
              className="bg-white rounded-xl p-6 flex flex-col items-center shadow"
            >
              <span className={`text-2xl font-semibold ${metric.color}`}>
                {metric.label === "Savings Rate"
                  ? metric.value
                  : `$${Number(metric.value).toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}`}
              </span>
              <span className="mt-2 text-xs tracking-widest text-gray-400 font-medium uppercase">
                {metric.label}
              </span>
            </div>
          ))}
        </div>
      </CardHeader>
      <CardContent>{renderChart()}</CardContent>
    </Card>
  );
};

export default ReportViewer;
